import { isEmpty, isNil } from 'lodash';
import { Fragment, useMemo } from 'react';
import { Button } from '@shared/components/Button';
import { SectionList } from '@shared/components/SectionList';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { CollectionViewAnalytics } from '@shared/scenes/cards/collectionView/CollectionViewAnalytics';
import { CollectionViewField } from '@shared/scenes/cards/collectionView/components/fields/CollectionViewField';
import { FieldVariant } from '@shared/scenes/cards/collectionView/components/fields/FieldVariant';
import { PersonRefsContext } from '@shared/scenes/cards/collectionView/components/fields/PersonRefsContext';
import { canStartStopTimer } from '@shared/scenes/cards/collectionView/components/fields/util/canStartStopTimer';
import { getFieldText } from '@shared/scenes/cards/collectionView/components/fields/util/getFieldText';
import { isInputButtonComponent, isTimeTrackerComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import {
  SubtableSlot,
  getSubtableRowNumber,
  getSubtableRowSlots,
  getSubtableSlotVariant,
} from './subtableColumns';
import { isDraftRow } from './subtableDrafts';
import { getRowPressProps } from './subtableRowPress';
import { SubtableRowRefs } from './useSubtableRows';
import styles from './SubtableList.module.css';

interface Props {
  /** The subtable's own template, which its input buttons' forms edit rows of. */
  workflowTemplateId: string;
  columns: API.AnyComponent[];
  rows: API.WorkflowEntityFragment[];
  refs?: SubtableRowRefs;
  /** The row open in the row menu, highlighted. */
  selectedRowId?: string;
  onRowPress: (entityId: string, index: number) => void;
  /**
   * Set by SubtableTableModal: every row, scrolling, loading more near the end. Without it, the
   * card form's preview: the first rows only, no scrolling, and "Show N more" for the rest.
   */
  modal?: boolean;
  /** Modal: whether another page can load, and loading it. */
  hasMore?: boolean;
  onLoadMore?: () => void;
  /** Card form: every row the subtable has (the "N" in "Show N more"), and showing them all. */
  totalCount?: number;
  onShowMore?: () => void;
  /**
   * Hides the controls that change a row from the list, like a timer's start/stop button or an
   * input button.
   */
  isReadOnly?: boolean;
}

/** The row's min-height (7Q, SubtableList.module.css), for rows the virtualizer hasn't measured. */
const ESTIMATED_ROW_HEIGHT = 56;
/** Rows the card form shows before "Show N more". */
const PREVIEW_ROW_COUNT = 4;

/**
 * Mobile: each row collapses to its number, a title, a secondary line and a trailing value. In the
 * card form it previews the first few rows; in the expanded modal it scrolls every row, virtualized,
 * loading the next page as it nears the end.
 */
export function SubtableList({
  workflowTemplateId,
  columns,
  rows,
  refs,
  selectedRowId,
  onRowPress,
  modal = false,
  hasMore = false,
  onLoadMore,
  totalCount = rows.length,
  onShowMore,
  isReadOnly = false,
}: Props) {
  const shownRows = useMemo(() => (modal ? rows : rows.slice(0, PREVIEW_ROW_COUNT)), [modal, rows]);
  const sections = useMemo(() => [{ data: shownRows }], [shownRows]);
  const moreCount = modal ? 0 : totalCount - shownRows.length;
  const hasShowMore = moreCount > 0;
  // One for every slot of every row: both depend only on the list.
  const analytics = useMemo(
    () => new CollectionViewAnalytics(workflowTemplateId, ''),
    [workflowTemplateId],
  );
  return (
    <PersonRefsContext.Provider value={refs?.personRefs}>
      <SectionList<API.WorkflowEntityFragment>
        role="list"
        className={[styles.subtableList, modal && styles.modal]}
        sections={sections}
        keyExtractor={row => row.id}
        estimatedItemHeight={ESTIMATED_ROW_HEIGHT}
        onEndReached={modal && hasMore ? onLoadMore : undefined}
        ListFooterComponent={
          hasShowMore && (
            <View className={styles.showMore}>
              <Button
                variant="ghost"
                bold
                size="medium"
                label={i18n('card.blocks.subtableInput.showMore', { count: moreCount })}
                onClick={onShowMore}
              />
            </View>
          )
        }
        renderItem={({ item: row, index }) => (
          <SubtableListRow
            workflowTemplateId={workflowTemplateId}
            row={row}
            index={index}
            isLast={!hasShowMore && index === shownRows.length - 1}
            columns={columns}
            refs={refs}
            onRowPress={onRowPress}
            selectedRowId={selectedRowId}
            isReadOnly={isReadOnly}
            analytics={analytics}
          />
        )}
      />
    </PersonRefsContext.Provider>
  );
}

function SubtableListRow({
  workflowTemplateId,
  row,
  index,
  columns,
  refs,
  isLast,
  onRowPress,
  selectedRowId,
  isReadOnly: isSubtableReadOnly,
  analytics,
}: Pick<
  Props,
  'workflowTemplateId' | 'columns' | 'refs' | 'selectedRowId' | 'onRowPress' | 'isReadOnly'
> & {
  row: API.WorkflowEntityFragment;
  index: number;
  isLast: boolean;
  analytics: CollectionViewAnalytics;
}) {
  const { title, secondary, trailing } = getSubtableRowSlots(row, columns, refs);
  // A draft row's controls wait until the row is created.
  const isReadOnly = isSubtableReadOnly || isDraftRow(row);
  const slotProps = { workflowTemplateId, rowId: row.id, analytics };
  return (
    <View
      row
      gap={1.5}
      role="listitem"
      {...getRowPressProps(() => onRowPress(row.id, index))}
      className={[styles.row, isLast && styles.last, row.id === selectedRowId && styles.selected]}
    >
      <Text size="body2" inactive className={styles.rowNumber}>
        {getSubtableRowNumber(index)}
      </Text>
      <View className={styles.text}>
        {!isNil(title) && (
          <SubtableSlotField
            {...slotProps}
            slot={title}
            className={styles.titleField}
            variant={getSubtableSlotVariant('title', title)}
            // The title keeps a timer to its time; its start/stop button is the trailing slot's.
            isReadOnly={isReadOnly || isTimeTrackerComponent(title.column)}
          />
        )}
        {!isEmpty(secondary) && <SubtableRowSecondary {...slotProps} slots={secondary} />}
      </View>
      {!isNil(trailing) && hasTrailingContent(trailing, isReadOnly) && (
        <View className={styles.trailing}>
          <SubtableSlotField
            {...slotProps}
            slot={trailing}
            className={styles.trailingField}
            variant={getSubtableSlotVariant('trailing', trailing)}
            isReadOnly={isReadOnly}
          />
        </View>
      )}
    </View>
  );
}

/**
 * Whether the trailing slot shows anything: a value, or a control that needs none (an input button
 * unless the subtable is read-only, a timer's start button unless it or its timer is). Otherwise
 * the slot is left out, so its gap doesn't narrow the title.
 */
function hasTrailingContent({ column, field }: SubtableSlot, isReadOnly?: boolean): boolean {
  if (!isEmpty(getFieldText(field))) {
    return true;
  }
  if (isInputButtonComponent(column)) {
    return !isReadOnly;
  }
  return canStartStopTimer(column, !isReadOnly);
}

/**
 * The secondary line: the middle columns' values, inline, joined by " · " and truncating as one
 * line.
 */
function SubtableRowSecondary({
  slots,
  workflowTemplateId,
  rowId,
  analytics,
}: {
  slots: SubtableSlot[];
  workflowTemplateId: string;
  rowId: string;
  analytics: CollectionViewAnalytics;
}) {
  return (
    <Text size="body2" inactive numberOfLines={1}>
      {slots.map((slot, index) => (
        <Fragment key={slot.column.id}>
          {index > 0 && ' · '}
          <SubtableSlotField
            slot={slot}
            variant={getSubtableSlotVariant('subtitle', slot)}
            workflowTemplateId={workflowTemplateId}
            rowId={rowId}
            analytics={analytics}
            isReadOnly
          />
        </Fragment>
      ))}
    </Text>
  );
}

/** A column's field in a slot, in the variant the slot asks for. */
function SubtableSlotField({
  slot,
  variant,
  className,
  workflowTemplateId,
  rowId,
  analytics,
  isReadOnly,
}: {
  slot: SubtableSlot;
  variant: FieldVariant;
  /** The slot's layout for the field's content. */
  className?: string;
  workflowTemplateId: string;
  rowId: string;
  analytics: CollectionViewAnalytics;
  isReadOnly: boolean | undefined;
}) {
  return (
    <CollectionViewField
      field={slot.field}
      component={slot.column}
      variant={variant}
      className={className}
      showTimerIcon
      workflowTemplateId={workflowTemplateId}
      entityId={rowId}
      canUpdateCard={!isReadOnly}
      analytics={analytics}
    />
  );
}
